import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import { applyMeta } from "../lib/meta";
import { Loader2 } from "lucide-react";
import { PromoBanner } from "../components/PromoBanner";
import { ReactionWall } from "../components/ReactionWall";
import {
  DEFAULT_SECTIONS, useGoogleFonts, Panel, StoreHero, ProductGrid, AboutSection, FaqSection, HoursSection, BalanceSection, CorporateCTA,
} from "../components/storefront/Sections";

const StoreError = ({ error }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#f6f8fb] px-6" data-testid="store-error">
    <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(20,30,55,0.08)] p-10 max-w-md text-center">
      <p className="text-sm font-semibold text-[#2ec9c9]">GiftVouchers.app</p>
      <p className="text-[#16243b] text-lg font-semibold mt-3">{error}</p>
      <p className="text-sm text-[#8a97a8] mt-2">Already have a voucher? You can still check it below.</p>
      <a href="/check-balance" className="gp-btn-teal inline-block rounded-full px-6 py-2.5 font-medium mt-6">Check a voucher</a>
    </div>
  </div>
);

const pageBackground = (d) => {
  const o = (d.bg_overlay || 0) / 100;
  if (d.bg_image) return { backgroundColor: d.bg_color || "#f6f8fb", backgroundImage: `linear-gradient(rgba(0,0,0,${o}),rgba(0,0,0,${o})), url("${d.bg_image}")`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" };
  return { background: d.bg_color || "#f6f8fb" };
};

export default function Storefront() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/store/${slug}`)
      .then(({ data }) => {
        setStore(data);
        const b = data.business;
        applyMeta({ title: b.seo_title || `${b.name} — Gift Vouchers`, description: b.seo_description || b.description, image: b.share_image || b.hero_image || b.logo_url });
      })
      .catch((e) => setError(e?.response?.status === 423 ? "This store is temporarily unavailable. Please check back soon." : "Store not found"));
  }, [slug]);

  const d = store?.business?.design || {};
  useGoogleFonts([d.heading_font, d.body_font]);

  if (error) return <StoreError error={error} />;
  if (!store) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;

  const b = store.business;
  const feats = store.features || [];
  const colors = { brand: b.brand_color || "#16243b", text: b.text_color || "#16243b", btnBg: b.button_color || "#2ec9c9", btnText: b.button_text_color || "#ffffff" };
  const hf = d.heading_font ? { fontFamily: `"${d.heading_font}", serif` } : {};
  const glass = !!d.bg_image;
  const props = { colors, hf };

  const render = {
    products: () => <Panel glass={glass} testid="store-section-products"><ProductGrid products={store.products} slug={slug} d={d} {...props} /></Panel>,
    about: () => (d.about?.text || d.about?.title) && <Panel glass={glass} testid="store-section-about"><AboutSection about={d.about} {...props} /></Panel>,
    reactions: () => store.reaction_wall?.length > 0 && <Panel glass={glass} testid="store-section-reactions"><ReactionWall items={store.reaction_wall} textColor={colors.text} /></Panel>,
    faq: () => d.faqs?.length > 0 && <Panel glass={glass} testid="store-section-faq"><FaqSection faqs={d.faqs} {...props} /></Panel>,
    hours: () => (d.hours?.length > 0 || d.address || d.phone) && <Panel glass={glass} testid="store-section-hours"><HoursSection d={d} {...props} /></Panel>,
    balance: () => <Panel testid="store-section-balance"><BalanceSection slug={slug} b={b} {...props} /></Panel>,
    corporate: () => feats.includes("corporate_portal") && <Panel testid="store-section-corporate"><CorporateCTA slug={slug} {...props} /></Panel>,
  };
  const sections = d.sections?.length ? d.sections : DEFAULT_SECTIONS;

  return (
    <div className="min-h-screen" style={{ ...pageBackground(d), ...(d.body_font ? { fontFamily: `"${d.body_font}", sans-serif` } : {}) }} data-testid="storefront-page">
      <PromoBanner promo={b.promo_banner} />
      <StoreHero b={b} d={d} brand={colors.brand} hf={hf} />
      <div className="pt-16">
        {sections.filter((s) => s.enabled && render[s.key]).map((s) => <React.Fragment key={s.key}>{render[s.key]()}</React.Fragment>)}
      </div>
      {(b.website || (b.social_links || []).length > 0) && (
        <Panel glass={glass} testid="store-links">
          <div className="flex flex-wrap items-center gap-4">
            {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="text-[#1fb0b0] font-medium hover:underline">Visit our website →</a>}
            {(b.social_links || []).filter((s) => s.url).map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-[#5a6b80] text-sm capitalize hover:text-[#16243b]">{s.platform}</a>
            ))}
          </div>
        </Panel>
      )}
      <footer className={`text-center py-8 text-sm ${glass ? "text-white/80" : "text-[#8a97a8]"}`}>Powered by GiftVouchers.app</footer>
    </div>
  );
}
